<script lang="ts">
export default {
  name: 'FilterRangeMenu',
};
</script>

<script setup lang="ts">
import { ref, watch } from 'vue';
import AppButton from '@/components/v2/AppButton.vue';
import NumberInput from '@/components/v2/NumberInput.vue';

const props = withDefaults(
  defineProps<{
    modelValue: number[];
    title: string;
    minTitle?: string;
    maxTitle?: string;
    /** When modelValue is empty, pre-populate inputs with this range (e.g. [0, max] from data). */
    suggestedRange?: number[];
    /** Optional formatter for hint text under inputs (e.g. compact money). */
    valueFormatterFn?: (value: number | undefined | null) => string | number;
    dataTestPrefix?: string;
  }>(),
  {
    minTitle: 'Minimum',
    maxTitle: 'Maximum',
    dataTestPrefix: 'range-filter',
  },
);

const emit = defineEmits<{
  'update:modelValue': [value: number[]];
  apply: [value: number[]];
}>();

const draftMin = ref<number | undefined>(undefined);
const draftMax = ref<number | undefined>(undefined);
const minError = ref('');
const maxError = ref('');

watch(
  () => [props.modelValue, props.suggestedRange] as const,
  ([val, suggested]) => {
    if (val?.length) {
      draftMin.value = val[0];
      draftMax.value = val[1];
    } else if (suggested != null && suggested.length >= 2) {
      draftMin.value = suggested[0];
      draftMax.value = suggested[1];
    } else {
      draftMin.value = undefined;
      draftMax.value = undefined;
    }
    clearErrors();
  },
  { immediate: true },
);

function clearErrors() {
  minError.value = '';
  maxError.value = '';
}

function setDraftMin(val: string | number | null | undefined) {
  draftMin.value = val === '' || val === null || val === undefined ? undefined : Number(val);
  if (draftMax.value != null && draftMin.value != null && draftMin.value > draftMax.value) {
    minError.value = 'Must be less than or equal to max';
    maxError.value = '';
  } else {
    clearErrors();
  }
}

function setDraftMax(val: string | number | null | undefined) {
  draftMax.value = val === '' || val === null || val === undefined ? undefined : Number(val);
  if (draftMin.value != null && draftMax.value != null && draftMax.value < draftMin.value) {
    maxError.value = 'Must be greater than or equal to min';
    minError.value = '';
  } else {
    clearErrors();
  }
}

function hasError(): boolean {
  return minError.value.length > 0 || maxError.value.length > 0;
}

function clear() {
  draftMin.value = undefined;
  draftMax.value = undefined;
  clearErrors();
  emit('update:modelValue', []);
  emit('apply', []);
}

function apply() {
  if (hasError()) return;
  const min = draftMin.value;
  const max = draftMax.value;
  const value = [min, max].filter((v) => v !== undefined && v !== null) as number[];
  emit('update:modelValue', value);
  emit('apply', value);
}

function hint(val: number | undefined): string {
  if (props.valueFormatterFn && val !== undefined && val !== null) {
    return String(props.valueFormatterFn(val));
  }
  return val !== undefined && val !== null ? String(val) : '';
}
</script>

<template>
  <div class="flex flex-col">
    <h4 class="border-b border-gray-200 px-4 py-3 text-sm font-medium text-gray-900">
      {{ title }}
    </h4>
    <div class="grid grid-cols-2 gap-3 px-4 py-3">
      <NumberInput
        :model-value="draftMin"
        :label="minTitle"
        :hint="hint(draftMin)"
        :error="!!minError"
        :error-message="minError"
        :data-testid="`${dataTestPrefix}-min`"
        @update:model-value="setDraftMin"
      />
      <NumberInput
        :model-value="draftMax"
        :label="maxTitle"
        :hint="hint(draftMax)"
        :error="!!maxError"
        :error-message="maxError"
        :data-testid="`${dataTestPrefix}-max`"
        @update:model-value="setDraftMax"
      />
    </div>
    <div class="flex justify-between gap-2 border-t border-gray-200 bg-gray-50 px-4 py-3">
      <AppButton
        outline
        :data-test="`${dataTestPrefix}-clear`"
        @click="clear">
        Clear
      </AppButton>
      <AppButton
        primary
        :disabled="hasError()"
        :data-test="`${dataTestPrefix}-apply`"
        @click="apply">
        Apply
      </AppButton>
    </div>
  </div>
</template>
